import { Fragment, type ReactNode } from "react";
import { Section } from "../components/Section";
import { LEGAL_LAST_UPDATED, type LegalDoc } from "../content/legal";
import type { PageKey } from "../content/meta";
import { usePageMeta } from "../lib/usePageMeta";
import styles from "./LegalPage.module.css";

/**
 * Renders a legal document from structured content.
 *
 * The DRAFT banner and its LEGAL_REVIEWED flag were removed on 2026-08-03: Ned
 * is a lawyer and these are his documents, so the "not yet reviewed by a lawyer"
 * disclaimer was never true of them.
 *
 * The [[placeholder]] highlighting stays. Nothing renders one today, but it is
 * the mechanism that stops an unconfirmed fact reaching a reader silently, and
 * it costs nothing while unused.
 */
export function LegalPage({ doc, page }: { doc: LegalDoc; page: PageKey }) {
  usePageMeta(page);

  return (
    /* The document title is the page's h1; every clause heading below is an h2
       under it. These are the pages most likely to be read start-to-finish by a
       screen reader — and by an LLM — and an outline starting at h2 hands both
       of them a document with no name. */
    <Section eyebrow={doc.eyebrow} heading={doc.title} tone="ink" level={1} heroScroll>
      <div className={styles.doc}>
        <div className={styles.intro}>
          {doc.intro.map((p, i) => (
            <p key={i}>{withPlaceholders(p)}</p>
          ))}
        </div>

        <p className={styles.updated}>Last updated: {LEGAL_LAST_UPDATED}</p>

        {doc.sections.map((s) => (
          <section key={s.h} className={styles.section}>
            <h2 className={styles.h}>{s.h}</h2>
            {s.body.map((p, i) => (
              <p key={i} className={styles.p}>
                {withPlaceholders(p)}
              </p>
            ))}
          </section>
        ))}
      </div>
    </Section>
  );
}

/** Wrap [[...]] spans in the highlight style; leave everything else as text. */
function withPlaceholders(text: string): ReactNode {
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return parts.map((part, i) =>
    /^\[\[[^\]]+\]\]$/.test(part) ? (
      <span key={i} className={styles.ph}>
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
